import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import { isStaff } from '../../lib/roles';
import type {
  ClienteRow,
  FormaPagoRow,
  ProductoRow,
  ProfesionalRow,
  ServicioRow,
  VentaLineaInput,
  VentaPagoInput,
} from '../../types/database.types';
import type { LineaVentaBorrador, PagoVentaBorrador, ProductoVentaItem } from '../../types/domain';

/**
 * Catálogo de productos que puede llegar a `AgregarLineaCard`: el
 * catálogo completo (staff, vía `productos` con RLS `productos_staff`)
 * o solo lo disponible para venta (profesional, vía el RPC
 * `productos_disponibles_venta()`). El componente solo lee
 * id/nombre/precio_venta de cada item, así que un tipo unión estructural
 * alcanza sin duplicar la UI.
 */
type ProductoParaVenta = ProductoRow | ProductoVentaItem;

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

let contadorLocal = 0;
function idLocal() {
  contadorLocal += 1;
  return `local-${contadorLocal}`;
}

/**
 * Registro de una venta nueva (DAF v2, sección 13-14): líneas de
 * servicios y/o productos, y uno o varios pagos. Todo se envía en una
 * sola llamada al RPC `registrar_venta`, que hace el total, la salida de
 * inventario de las líneas de producto y la validación de los pagos de
 * forma atómica en el servidor (ver ARQUITECTURA_FRONTEND.md sección 4 y
 * supabase/schema_ventas_inventario.sql) — el frontend nunca calcula el
 * total "de verdad", solo lo estima para mostrarlo mientras se arma la
 * venta.
 *
 * Cartera de clientas (pedido explícito del usuario, no en el DAF v2
 * original — ver supabase/schema_cartera_clientes.sql): los pagos ya NO
 * tienen que sumar exactamente el total. Si la casilla "Fiar el saldo
 * restante" está marcada y sobra un monto sin pagar, el servidor lo
 * registra solo como una cuenta por cobrar de la clienta seleccionada
 * (siempre hay una, porque el selector de clienta es obligatorio). Un
 * pago que SUPERE el total nunca se acepta, se marque o no la casilla.
 * Fiar es exclusivo de staff (ver `staff` más abajo): extender crédito a
 * una clienta sigue siendo una decisión de recepción/admin, así que a
 * una profesional ni siquiera se le muestra la casilla — RLS
 * (`cxc_staff`, sin política para profesional) lo respalda del lado del
 * servidor, ver schema_profesional_ventas_agenda.sql.
 *
 * Profesional vendiendo lo suyo (pedido explícito del usuario, la misma
 * migración): una profesional puede llegar aquí de dos formas. (1) Por
 * el link "Nueva venta" normal, sin nada precargado. (2) Desde Agenda.tsx,
 * justo después de marcar su propia cita como "Atendida" — en ese caso
 * llega con `?cliente=&cita=&servicio=&profesional=` en la URL y el
 * formulario precarga la clienta y una línea con ese servicio ya lista,
 * para que solo tenga que revisar y cobrar. En ambos casos, la línea de
 * servicio que agregue queda SIEMPRE atribuida a ella misma (el selector
 * "profesional que lo realizó" se oculta y se fija solo) — RLS
 * (`venta_detalle_profesional_insert`) lo exige igual del lado del
 * servidor.
 */
export function VentaForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { perfil } = useAuth();
  const staff = isStaff(perfil?.rol);
  const esProfesional = perfil?.rol === 'profesional';

  const citaIdPrecarga = searchParams.get('cita');
  const clienteIdPrecarga = searchParams.get('cliente');
  const servicioIdPrecarga = searchParams.get('servicio');

  const [clientes, setClientes] = useState<ClienteRow[]>([]);
  const [servicios, setServicios] = useState<ServicioRow[]>([]);
  const [productos, setProductos] = useState<ProductoParaVenta[]>([]);
  const [profesionales, setProfesionales] = useState<ProfesionalRow[]>([]);
  const [formasPago, setFormasPago] = useState<FormaPagoRow[]>([]);

  const [clienteId, setClienteId] = useState('');
  const [lineas, setLineas] = useState<LineaVentaBorrador[]>([]);
  const [pagos, setPagos] = useState<PagoVentaBorrador[]>([]);
  const [fiarSaldo, setFiarSaldo] = useState(false);
  const [precargada, setPrecargada] = useState(false);

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Productos: staff sigue viendo el catálogo completo directo de la
    // tabla (RLS `productos_staff` ya la protege). Una profesional lee a
    // través de `productos_disponibles_venta()` -- ni RLS de fila normal
    // le serviría aquí, porque igual expondría costo y stock exacto en
    // cada fila; ver schema_profesional_productos_inventario.sql.
    const productosQuery = staff
      ? supabase.from('productos').select('*').eq('estado', 'activo').order('nombre')
      : supabase.rpc('productos_disponibles_venta');

    Promise.all([
      supabase.from('clientes').select('*').eq('estado', 'activo').order('nombre'),
      supabase.from('servicios').select('*').eq('estado', 'activo').order('nombre'),
      productosQuery,
      supabase.from('profesionales').select('*').eq('estado', 'activo').order('nombre'),
      supabase.from('formas_pago').select('*').eq('estado', 'activo').order('nombre'),
    ]).then(([c, s, p, pr, fp]) => {
      if (c.data) setClientes(c.data);
      if (s.data) setServicios(s.data);
      if (p.data) setProductos(staff ? (p.data as ProductoRow[]) : (p.data as ProductoVentaItem[]).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      if (pr.data) setProfesionales(pr.data);
      if (fp.data) setFormasPago(fp.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff]);

  // Precarga desde el flujo "marcar atendida → registrar venta"
  // (Agenda.tsx). Espera a que `servicios` esté cargado para poder tomar
  // el precio del servicio; se ejecuta una sola vez (`precargada`).
  useEffect(() => {
    if (precargada || !clienteIdPrecarga || !servicioIdPrecarga || servicios.length === 0) return;
    const servicio = servicios.find((s) => s.id === servicioIdPrecarga);
    if (!servicio) return;

    setClienteId(clienteIdPrecarga);
    setLineas((ls) => [
      ...ls,
      {
        key: `precarga-${servicio.id}`,
        tipo: 'servicio',
        itemId: servicio.id,
        nombre: servicio.nombre,
        cantidad: 1,
        precioUnitario: servicio.precio,
        profesionalId: perfil?.profesionalId ?? null,
      },
    ]);
    setPrecargada(true);
  }, [precargada, clienteIdPrecarga, servicioIdPrecarga, servicios, perfil]);

  const total = lineas.reduce((sum, l) => sum + l.cantidad * l.precioUnitario, 0);
  const totalPagos = pagos.reduce((sum, p) => sum + p.monto, 0);
  // Saldo que quedaría sin pagar si se guarda la venta tal como está ahora
  // (positivo = falta por pagar, negativo = los pagos superan el total).
  const saldoRestante = total - totalPagos;
  const pagoExacto = Math.abs(saldoRestante) < 1;
  const sobrepago = saldoRestante < -1;
  const hayCredito = !pagoExacto && !sobrepago && saldoRestante > 1;
  // Válida para enviar: o los pagos cuadran exactos (como siempre), o
  // (solo para staff) queda un saldo real y se marcó "fiar el saldo
  // restante" — una profesional siempre tiene que cuadrar el pago exacto
  // (ver docstring: fiar sigue siendo exclusivo de staff).
  const puedeFiar = staff;
  const pagosCuadran = pagoExacto || (puedeFiar && fiarSaldo && !sobrepago && (pagos.length > 0 || total > 0));

  function agregarLinea(linea: Omit<LineaVentaBorrador, 'key'>) {
    setLineas((ls) => [...ls, { ...linea, key: idLocal() }]);
  }

  function quitarLinea(key: string) {
    setLineas((ls) => ls.filter((l) => l.key !== key));
  }

  function agregarPago(pago: Omit<PagoVentaBorrador, 'key'>) {
    setPagos((ps) => [...ps, { ...pago, key: idLocal() }]);
  }

  function quitarPago(key: string) {
    setPagos((ps) => ps.filter((p) => p.key !== key));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    setError(null);

    if (!clienteId) {
      setError('Selecciona una clienta');
      return;
    }
    if (lineas.length === 0) {
      setError('Agrega al menos una línea a la venta');
      return;
    }
    if (!pagosCuadran) {
      if (sobrepago) {
        setError(`Los pagos (${COP.format(totalPagos)}) no pueden superar el total de la venta (${COP.format(total)})`);
      } else if (puedeFiar) {
        setError(
          `Los pagos (${COP.format(totalPagos)}) no cubren el total (${COP.format(total)}). Agrega más pagos o marca "Fiar el saldo restante" para dejar la diferencia (${COP.format(saldoRestante)}) en la cartera de la clienta.`,
        );
      } else {
        setError(`Los pagos (${COP.format(totalPagos)}) no cubren el total (${COP.format(total)}). Agrega más pagos hasta completarlo.`);
      }
      return;
    }

    const p_lineas: VentaLineaInput[] = lineas.map((l) => ({
      tipo: l.tipo,
      servicio_id: l.tipo === 'servicio' ? l.itemId : null,
      producto_id: l.tipo === 'producto' ? l.itemId : null,
      cantidad: l.cantidad,
      precio_unitario: l.precioUnitario,
      profesional_id: l.profesionalId,
    }));

    const p_pagos: VentaPagoInput[] = pagos.map((p) => ({
      forma_pago_id: p.formaPagoId,
      monto: p.monto,
    }));

    setGuardando(true);
    const { error: err } = await supabase.rpc('registrar_venta', {
      p_salon_id: perfil.salon_id,
      p_cliente_id: clienteId,
      p_cita_id: citaIdPrecarga || null,
      p_lineas,
      p_pagos,
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/ventas');
  }

  return (
    <div className="max-w-3xl">
      <PageHeader title="Nueva venta" />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="card">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-onyx/80">Clienta *</span>
            <select required className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="" disabled>
                Selecciona una clienta
              </option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre} {c.apellido ?? ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <AgregarLineaCard
          servicios={servicios}
          productos={productos}
          profesionales={profesionales}
          profesionalFija={esProfesional ? { id: perfil?.profesionalId ?? '', nombre: perfil?.nombre ?? 'Yo' } : null}
          onAgregar={agregarLinea}
        />

        <div className="card">
          <p className="mb-2 text-sm font-medium text-onyx/80">Líneas de la venta</p>
          {lineas.length === 0 && <p className="text-sm text-onyx/50">Todavía no has agregado nada.</p>}
          {lineas.length > 0 && (
            <table className="w-full text-sm">
              <tbody>
                {lineas.map((l) => (
                  <tr key={l.key} className="border-b border-onyx/5 last:border-0">
                    <td className="py-2">
                      <span className="badge mr-2 bg-onyx/5 text-onyx/60">
                        {l.tipo === 'servicio' ? 'Servicio' : 'Producto'}
                      </span>
                      {l.nombre}
                    </td>
                    <td className="py-2 text-onyx/60">x{l.cantidad}</td>
                    <td className="py-2 text-right text-onyx/70">{COP.format(l.cantidad * l.precioUnitario)}</td>
                    <td className="py-2 pl-3 text-right">
                      <button type="button" className="text-red-600 hover:underline" onClick={() => quitarLinea(l.key)}>
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex justify-end border-t border-onyx/10 pt-3">
            <p className="font-display text-xl text-gold-deep">Total: {COP.format(total)}</p>
          </div>
        </div>

        <AgregarPagoCard formasPago={formasPago} totalPendiente={total - totalPagos} onAgregar={agregarPago} />

        <div className="card">
          <p className="mb-2 text-sm font-medium text-onyx/80">Pagos</p>
          {pagos.length === 0 && <p className="text-sm text-onyx/50">Todavía no has agregado ningún pago.</p>}
          {pagos.length > 0 && (
            <ul className="space-y-1">
              {pagos.map((p) => {
                const fp = formasPago.find((f) => f.id === p.formaPagoId);
                return (
                  <li key={p.key} className="flex items-center justify-between text-sm">
                    <span>{fp?.nombre ?? '—'}</span>
                    <span className="flex items-center gap-3">
                      {COP.format(p.monto)}
                      <button type="button" className="text-red-600 hover:underline" onClick={() => quitarPago(p.key)}>
                        Quitar
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <p className={`mt-2 text-sm ${pagosCuadran ? 'text-green-700' : 'text-onyx/50'}`}>
            Pagado: {COP.format(totalPagos)} de {COP.format(total)}
          </p>

          {puedeFiar && !pagoExacto && !sobrepago && total > 0 && (
            <label className="mt-3 flex items-start gap-2 border-t border-onyx/10 pt-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={fiarSaldo}
                onChange={(e) => setFiarSaldo(e.target.checked)}
              />
              <span>
                Fiar el saldo restante a la clienta ({COP.format(Math.max(saldoRestante, 0))})
                <span className="block text-xs text-onyx/50">
                  Queda registrado en la Cartera de clientas como una cuenta por cobrar pendiente de pago.
                </span>
              </span>
            </label>
          )}

          {hayCredito && fiarSaldo && (
            <p className="mt-2 text-xs text-amber-700">
              Esta venta va a quedar con {COP.format(saldoRestante)} en cartera a nombre de la clienta seleccionada.
            </p>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Registrar venta'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/ventas')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

interface AgregarLineaCardProps {
  servicios: ServicioRow[];
  productos: ProductoParaVenta[];
  profesionales: ProfesionalRow[];
  /** Si viene, quien agrega es una profesional: cada línea de servicio
   * queda atribuida a ella misma, sin selector -- RLS
   * (`venta_detalle_profesional_insert`) lo exige igual del lado del
   * servidor, esto solo evita ofrecerle una opción que le sería
   * rechazada. */
  profesionalFija: { id: string; nombre: string } | null;
  onAgregar: (linea: Omit<LineaVentaBorrador, 'key'>) => void;
}

function AgregarLineaCard({ servicios, productos, profesionales, profesionalFija, onAgregar }: AgregarLineaCardProps) {
  const [tipo, setTipo] = useState<'servicio' | 'producto'>('servicio');
  const [itemId, setItemId] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [precio, setPrecio] = useState('0');
  const [profesionalId, setProfesionalId] = useState('');

  const catalogo = tipo === 'servicio' ? servicios : productos;

  function seleccionarItem(id: string) {
    setItemId(id);
    const item = catalogo.find((i) => i.id === id);
    if (item) {
      const precioItem = 'precio' in item ? item.precio : item.precio_venta;
      setPrecio(String(precioItem));
    }
  }

  function handleAgregar() {
    const item = catalogo.find((i) => i.id === itemId);
    if (!item || !cantidad || Number(cantidad) <= 0) return;

    onAgregar({
      tipo,
      itemId,
      nombre: item.nombre,
      cantidad: Number(cantidad),
      precioUnitario: Number(precio),
      profesionalId: tipo === 'servicio' ? (profesionalFija ? profesionalFija.id : profesionalId || null) : null,
    });

    setItemId('');
    setCantidad('1');
    setPrecio('0');
    setProfesionalId('');
  }

  return (
    <div className="card space-y-3">
      <p className="text-sm font-medium text-onyx/80">Agregar línea</p>

      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={tipo === 'servicio'}
            onChange={() => {
              setTipo('servicio');
              setItemId('');
              setPrecio('0');
            }}
          />
          Servicio
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={tipo === 'producto'}
            onChange={() => {
              setTipo('producto');
              setItemId('');
              setPrecio('0');
            }}
          />
          Producto
        </label>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <select className="input-field col-span-2" value={itemId} onChange={(e) => seleccionarItem(e.target.value)}>
          <option value="">Selecciona {tipo === 'servicio' ? 'un servicio' : 'un producto'}</option>
          {catalogo.map((i) => (
            <option key={i.id} value={i.id}>
              {i.nombre}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          step="0.01"
          className="input-field"
          placeholder="Cantidad"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
        <input
          type="number"
          min={0}
          step={100}
          className="input-field"
          placeholder="Precio unitario"
          value={precio}
          onChange={(e) => setPrecio(e.target.value)}
        />
      </div>

      {tipo === 'servicio' && profesionalFija && (
        <p className="text-sm text-onyx/60">
          Profesional que lo realizó: <span className="font-medium text-onyx">{profesionalFija.nombre}</span>
        </p>
      )}
      {tipo === 'servicio' && !profesionalFija && (
        <select className="input-field" value={profesionalId} onChange={(e) => setProfesionalId(e.target.value)}>
          <option value="">Profesional que lo realizó (opcional)</option>
          {profesionales.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </select>
      )}

      <button type="button" className="btn-secondary" onClick={handleAgregar} disabled={!itemId}>
        Agregar a la venta
      </button>
    </div>
  );
}

interface AgregarPagoCardProps {
  formasPago: FormaPagoRow[];
  totalPendiente: number;
  onAgregar: (pago: Omit<PagoVentaBorrador, 'key'>) => void;
}

function AgregarPagoCard({ formasPago, totalPendiente, onAgregar }: AgregarPagoCardProps) {
  const [formaPagoId, setFormaPagoId] = useState('');
  const [monto, setMonto] = useState('');

  function handleAgregar() {
    const montoNum = Number(monto);
    if (!formaPagoId || !montoNum || montoNum <= 0) return;
    onAgregar({ formaPagoId, monto: montoNum });
    setFormaPagoId('');
    setMonto('');
  }

  return (
    <div className="card space-y-3">
      <p className="text-sm font-medium text-onyx/80">Agregar pago</p>
      <div className="grid grid-cols-3 gap-3">
        <select className="input-field" value={formaPagoId} onChange={(e) => setFormaPagoId(e.target.value)}>
          <option value="">Forma de pago</option>
          {formasPago.map((f) => (
            <option key={f.id} value={f.id}>
              {f.nombre}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          step={100}
          className="input-field"
          placeholder={totalPendiente > 0 ? `Sugerido: ${Math.round(totalPendiente)}` : 'Monto'}
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
        />
        <button type="button" className="btn-secondary" onClick={handleAgregar} disabled={!formaPagoId}>
          Agregar pago
        </button>
      </div>
    </div>
  );
}
